import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pedidosService } from '@/api/pedidos';
import Layout from '@/components/layout/Layout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useEmpresaStore } from '@/store/empresaStore';

type FilterChip = 'todos' | 0 | 2;

export default function PedidosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message;
  const empresaSeleccionada = useEmpresaStore((state) => state.empresaSeleccionada);

  const [activeFilter, setActiveFilter] = useState<FilterChip>('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(20);

  const { data: pedidosResponse, isLoading } = useQuery({
    queryKey: ['pedidos', empresaSeleccionada?.id, limit],
    queryFn: () => pedidosService.getPedidos({ empresa: empresaSeleccionada?.id, limit }),
    enabled: !!empresaSeleccionada,
  });

  const pedidos = pedidosResponse?.data || [];

  const filters: { label: string; value: FilterChip }[] = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendiente', value: 0 },
    { label: 'Servido', value: 2 },
  ];

  const filteredPedidos = pedidos.filter((pedido) => {
    const matchesFilter = activeFilter === 'todos' || pedido.estado === activeFilter;
    const matchesSearch =
      searchQuery === '' ||
      pedido.numero.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pedido.clienteNombre?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getEstadoBadgeColor = (estado: number) => {
    const colors: Record<number, string> = {
      0: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
      2: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
  };

  const getEstadoLabel = (estado: number) => {
    const labels: Record<number, string> = {
      0: 'Pendiente',
      2: 'Servido',
    };
    return labels[estado] || 'Desconocido';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Layout
      title="Mis Pedidos"
      subtitle="Consulta el historial y el estado de tus pedidos"
      showBack
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
    >
      {successMessage && (
        <div className="mx-4 mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
          {successMessage}
        </div>
      )}

      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                activeFilter === filter.value
                  ? 'border-[#27648d] bg-[#27648d] text-white'
                  : 'border-[#dbe1ea] bg-white text-[#27648d] hover:border-[#27648d]'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <label className="flex w-fit items-center gap-3 text-sm text-slate-600 sm:ml-auto">
          <span>Mostrar</span>
          <div className="relative">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="appearance-none rounded-xl border-2 border-[#27648d] bg-white py-2 pl-4 pr-10 text-sm font-semibold text-[#27648d] outline-none"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[#27648d]">
              <span className="material-symbols-outlined text-base">keyboard_arrow_down</span>
            </span>
          </div>
          <span>pedidos</span>
        </label>
      </div>

      <div className="flex-1 px-4 pb-24">
        {isLoading ? (
          <LoadingSpinner />
        ) : filteredPedidos && filteredPedidos.length > 0 ? (
          <div className="space-y-3">
            {filteredPedidos.map((pedido) => (
              <div
                key={pedido.id}
                onClick={() => navigate(`/pedidos/${pedido.id}`)}
                className="cursor-pointer rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div>
                    <p className="text-base font-bold text-[#0d141b] dark:text-white">{pedido.numero}</p>
                    <p className="text-sm text-[#4c739a] dark:text-slate-400">
                      {pedido.clienteNombre || `Cliente ${pedido.clienteId}`}
                    </p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${getEstadoBadgeColor(pedido.estado)}`}>
                    {getEstadoLabel(pedido.estado)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-[#4c739a] dark:text-slate-400">
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-base">calendar_today</span>
                      <span>{formatDate(pedido.fecha)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-base">inventory_2</span>
                      <span>{pedido.cantidadArticulos ?? 0} artículos</span>
                    </div>
                  </div>
                  <p className="font-bold text-[#0d141b] dark:text-white">{pedido.total.toFixed(2)}€</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
            <span className="material-symbols-outlined mb-4 text-6xl text-slate-400">receipt_long</span>
            <h3 className="mb-2 text-xl font-bold">No hay pedidos</h3>
            <p className="mb-6 text-center text-slate-500">
              {searchQuery
                ? 'No se encontraron pedidos con tu búsqueda'
                : activeFilter !== 'todos'
                  ? `No hay pedidos con estado "${activeFilter}"`
                  : 'Aún no has creado ningún pedido'}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
